import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Modal from '../ui/Modal.jsx';
import Button from '../ui/Button.jsx';
import LevelBadge from './LevelBadge.jsx';
import { getLevelName } from '../../constants/auraLevels';
import { haptic } from '../../utils/haptics';

/**
 * Level-Up-Overlay: der Otter mit seiner neuen Welle (OtterFigure ueber
 * LevelBadge), gross, in dem Moment, in dem die Schwelle faellt.
 *
 * Bis September 2026 war ein Level-Aufstieg im Web ein gruener Standard-Toast
 * (und der hing an "Push bei Aura-Updates", Default aus - also meist gar nichts).
 * Das Overlay ist ein In-App-Ereignis, kein Push: Es erscheint auf Web und in
 * der nativen App unabhaengig von den Push-Einstellungen. Gesteuert aus dem
 * NotificationContext (data.kind === 'LEVEL_UP').
 */
const LevelUpCelebration = ({ levelUp, onClose }) => {
  const navigate = useNavigate();
  const open = Boolean(levelUp);

  useEffect(() => {
    if (open) haptic.levelUp();
  }, [open]);

  if (!open) return null;

  const { oldLevel, newLevel } = levelUp;
  const name = getLevelName(newLevel);
  // „Neues Level 3: Strömung“ – die Wellen-Namen sind keine Personen-Titel mehr
  // (vorher „Du bist jetzt Mentor“, generisches Maskulinum).
  const headline = `Neues Level ${newLevel}: ${name}`;

  const goToAura = () => {
    onClose();
    navigate('/aura-stats');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel={`Level-Aufstieg. ${headline}`}
      size="sm"
      hideClose
      bodyClassName="p-6"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Weiter</Button>
          <Button variant="primary" onClick={goToAura}>Zum Aura-Status</Button>
        </>
      )}
    >
      <div className="flex flex-col items-center text-center">
        {/* Bungee wie im Spot: Ein Level-Aufstieg ist einer der wenigen Momente,
            in denen die App laut sein darf (CD Regel 4: Bungee fuer Highlights). */}
        <p className="font-display text-[22px] uppercase leading-none tracking-[0.04em] text-primary">Level-Up!</p>
        <div className="my-4 animate-pop-in motion-reduce:animate-none">
          <LevelBadge level={newLevel} size="xl" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">{headline}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {oldLevel
            ? `Du bist von Level ${oldLevel} auf Level ${newLevel} aufgestiegen.`
            : `Du hast Level ${newLevel} erreicht.`}
        </p>
      </div>
    </Modal>
  );
};

LevelUpCelebration.propTypes = {
  levelUp: PropTypes.shape({
    oldLevel: PropTypes.number,
    newLevel: PropTypes.number.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
};

export default LevelUpCelebration;
